import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../api/api';
import { getTokenData } from 'src/utils/api';
import { LOADING_STATUSES } from '../../../constants/statuses';
import { isResponseSuccess } from '../../../utils/api';
import { CATEGORIES_FOR_PRESETS_TYPE, ALL_CATEGORY } from '../constants/presetCategories';

export const loadPresetCategories = createAsyncThunk(
  'presetCategories/loadPresetCategories',
  async ({ type }, { getState, rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).PRESET_CATEGORIES.load({ type });
      if (isResponseSuccess(response.status)) {
        return { data: response.data };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const loadPresetCategoryById = createAsyncThunk(
  'presetCategories/loadPresetCategoryById',
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).PRESET_CATEGORIES.loadById(id);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const createPresetCategory = createAsyncThunk(
  'presetCategories/createPresetCategory',
  async (data, { getState, rejectWithValue }) => {
    if (data.isSystemCategory) {
      return rejectWithValue('Cannot create system category');
    }

    try {
      const response = await API(getTokenData()).PRESET_CATEGORIES.create(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const updatePresetCategory = createAsyncThunk(
  'presetCategories/updatePresetCategory',
  async (data, { getState, rejectWithValue }) => {
    if (data.isSystemCategory || data.id === ALL_CATEGORY.id) {
      return rejectWithValue('Cannot update system category');
    }

    try {
      const response = await API(getTokenData()).PRESET_CATEGORIES.update(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return rejectWithValue('Error during update');
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const deletePresetCategory = createAsyncThunk(
  'presetCategories/deletePresetCategory',
  async (id, { getState, rejectWithValue }) => {
    if (id === ALL_CATEGORY.id) {
      return rejectWithValue('Cannot delete system category');
    }

    try {
      const response = await API(getTokenData()).PRESET_CATEGORIES.delete(id);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return rejectWithValue('Error deleting');
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const reorderPresetCategories = createAsyncThunk(
  'presetCategories/reorderPresetCategories',
  async ({ type, ids }, { getState, rejectWithValue }) => {
    try {
      const filteredIds = ids.filter((id) => id !== ALL_CATEGORY.id);
      const response = await API(getTokenData())
        .PRESET_CATEGORIES.reorder(type, filteredIds);
      if (isResponseSuccess(response.status)) {
        return { type, ids: filteredIds };
      }
      return rejectWithValue('Sorting error');
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

const initialState = {
  sourceCategories: [],
  networksCategories: [],
  selected: null,
  status: {
    source: LOADING_STATUSES.IDLE,
    network: LOADING_STATUSES.IDLE,
  },
  selectedStatus: LOADING_STATUSES.IDLE,
  error: null,
};

const presetCategoriesSlice = createSlice({
  name: 'presetCategories',
  initialState,
  reducers: {
    resetPresetCategories: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPresetCategories.pending, (state, action) => {
        const type = action?.meta.arg?.type;
        if (type) {
          state.status[type] = LOADING_STATUSES.LOADING;
        }
        state.error = null;
      })
      .addCase(loadPresetCategories.fulfilled, (state, action) => {
        const type = action?.meta.arg?.type;
        const allCategories = action.payload?.data || [];
        if (type === 'source') {
          const sourceCategories = allCategories
            .filter((cat) => cat.type === CATEGORIES_FOR_PRESETS_TYPE.source);
          const allCategory = { ...ALL_CATEGORY, type: CATEGORIES_FOR_PRESETS_TYPE.source };
          state.sourceCategories = [allCategory, ...sourceCategories];
          state.status.source = LOADING_STATUSES.SUCCEEDED;
        } else if (type === 'network') {
          const networkCategories = allCategories
            .filter((cat) => cat.type === CATEGORIES_FOR_PRESETS_TYPE.network);
          const allCategory = { ...ALL_CATEGORY, type: CATEGORIES_FOR_PRESETS_TYPE.network };
          state.networksCategories = [allCategory, ...networkCategories];
          state.status.network = LOADING_STATUSES.SUCCEEDED;
        }
      })
      .addCase(loadPresetCategories.rejected, (state, action) => {
        const type = action?.meta.arg?.type;
        if (type) {
          state.status[type] = LOADING_STATUSES.FAILED;
        }
        state.error = action.payload || action.error.message;
      })
      .addCase(loadPresetCategoryById.pending, (state) => {
        state.selectedStatus = LOADING_STATUSES.LOADING;
      })
      .addCase(loadPresetCategoryById.fulfilled, (state, action) => {
        state.selected = action.payload || null;
        state.selectedStatus = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadPresetCategoryById.rejected, (state, action) => {
        state.selectedStatus = LOADING_STATUSES.FAILED;
        state.error = action.payload || action.error.message;
      })
      .addCase(createPresetCategory.fulfilled, (state, action) => {
        const newCategory = action.payload;
        if (newCategory.type === CATEGORIES_FOR_PRESETS_TYPE.source) {
          state.sourceCategories = [
            state.sourceCategories[0],
            ...state.sourceCategories.slice(1),
            newCategory,
          ];
          state.status.source = LOADING_STATUSES.SUCCEEDED;
        } else if (newCategory.type === CATEGORIES_FOR_PRESETS_TYPE.network) {
          state.networksCategories = [
            state.networksCategories[0],
            ...state.networksCategories.slice(1),
            newCategory,
          ];
          state.status.network = LOADING_STATUSES.SUCCEEDED;
        }
      })
      .addCase(updatePresetCategory.fulfilled, (state, action) => {
        const updatedCategory = action.payload;
        if (updatedCategory.isSystemCategory) {
          return;
        }
        if (updatedCategory.type === CATEGORIES_FOR_PRESETS_TYPE.source) {
          state.sourceCategories = state.sourceCategories
            .map((cat) => (cat.id === updatedCategory.id ? updatedCategory : cat));
          state.status.source = LOADING_STATUSES.SUCCEEDED;
        } else if (updatedCategory.type === CATEGORIES_FOR_PRESETS_TYPE.network) {
          state.networksCategories = state.networksCategories
            .map((cat) => (cat.id === updatedCategory.id ? updatedCategory : cat));
          state.status.network = LOADING_STATUSES.SUCCEEDED;
        }
      })
      .addCase(deletePresetCategory.fulfilled, (state, action) => {
        const deletedId = action.meta.arg;
        if (deletedId === ALL_CATEGORY.id) {
          return;
        }
        state.sourceCategories = state.sourceCategories.filter((cat) => cat.id !== deletedId);
        state.networksCategories = state.networksCategories.filter((cat) => cat.id !== deletedId);
      })
      .addCase(reorderPresetCategories.fulfilled, (state, action) => {
        const { type, ids } = action.payload;
        if (type === CATEGORIES_FOR_PRESETS_TYPE.source) {
          const allCategory = state.sourceCategories.find((cat) => cat.id === ALL_CATEGORY.id);
          const reorderedCategories = ids
            .map((id) => state.sourceCategories.find((cat) => cat.id === id))
            .filter(Boolean);
          state.sourceCategories = allCategory
            ? [allCategory, ...reorderedCategories] : reorderedCategories;
        } else if (type === CATEGORIES_FOR_PRESETS_TYPE.network) {
          const allCategory = state.networksCategories.find((cat) => cat.id === ALL_CATEGORY.id);
          const reorderedCategories = ids
            .map((id) => state.networksCategories.find((cat) => cat.id === id))
            .filter(Boolean);
          state.networksCategories = allCategory
            ? [allCategory, ...reorderedCategories] : reorderedCategories;
        }
      });
  },
});

export const { resetPresetCategories } = presetCategoriesSlice.actions;
export default presetCategoriesSlice.reducer;
